import { useState, useCallback } from 'react';
import type { UseSurveyStateReturn, ResponsesMap, ErrorsMap, ResponseValue } from '../SurveyPage.types';

/**
 * Custom hook for managing survey state (responses, current question, errors)
 *
 * This hook encapsulates all state management logic for the survey, providing
 * a clean interface for navigating questions, updating responses, and managing
 * validation errors.
 *
 * @param totalQuestions - Total number of questions in the survey
 * @returns Object containing state and state management functions
 *
 * @example
 * const { currentQuestionIndex, responses, updateResponse, goToNext } = useSurveyState(10);
 */
export const useSurveyState = (totalQuestions: number): UseSurveyStateReturn => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<ResponsesMap>({});
  const [errors, setErrors] = useState<ErrorsMap>({});

  /**
   * Navigate to the next question if not at the end
   */
  const goToNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, totalQuestions]);

  /**
   * Navigate to the previous question if not at the beginning
   */
  const goToPrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  /**
   * Update a response for a specific question and clear any associated error
   */
  const updateResponse = useCallback((questionNumber: number, value: ResponseValue) => {
    setResponses(prev => ({ ...prev, [questionNumber]: value }));

    // Clear error when user starts answering
    setErrors(prev => {
      if (prev[questionNumber]) {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      }
      return prev;
    });
  }, []);

  /**
   * Clear a specific validation error
   */
  const clearError = useCallback((questionNumber: number) => {
    setErrors(prev => {
      if (prev[questionNumber]) {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      }
      return prev;
    });
  }, []);

  /**
   * Reset question index to a specific value (useful for page changes)
   */
  const setQuestionIndex = useCallback((index: number) => {
    setCurrentQuestionIndex(index);
  }, []);

  return {
    currentQuestionIndex,
    responses,
    errors,
    goToNext,
    goToPrevious,
    updateResponse,
    setErrors,
    clearError,
    setQuestionIndex,
  };
};
